// Pure builders for the "add to calendar" links surfaced after a feed is
// minted. The feed URL from the API is an `https://` `.ics` subscription link;
// each calendar app wants it wrapped differently.

/** Swaps the scheme to `webcal://` — the scheme Apple Calendar (and others)
 * register for subscription feeds. Leaves a non-http(s) URL untouched. */
export function webcalUrl(feedUrl: string): string {
  return feedUrl.replace(/^https?:\/\//i, 'webcal://');
}

/** Google Calendar "add by URL" deep link. Google expects the feed URL in the
 * `cid` param; it accepts the `webcal://` form. */
export function googleCalendarUrl(feedUrl: string): string {
  return `https://calendar.google.com/calendar/render?cid=${encodeURIComponent(
    webcalUrl(feedUrl),
  )}`;
}

/** Outlook.com "add from web" deep link. */
export function outlookCalendarUrl(feedUrl: string, name: string): string {
  const params = new URLSearchParams({ url: feedUrl, name });
  return `https://outlook.live.com/calendar/0/addfromweb?${params.toString()}`;
}

/** Returns `feedUrl` with `?employee=<id>` set (owner feeds only). A falsy id
 * is a no-op; an existing `employee` param is overwritten. */
export function withEmployeeParam(feedUrl: string, employeeId?: string): string {
  if (!employeeId) return feedUrl;
  try {
    const url = new URL(feedUrl);
    url.searchParams.set('employee', employeeId);
    return url.toString();
  } catch {
    return feedUrl;
  }
}

/** "Connected since" date — day granularity, from an RFC 3339 timestamp. */
export function formatFeedDate(iso: string | null): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}
